/**
 * Der Kartengrund: Karte oder Luftbild — für alle drei Karten dieselbe Wahl.
 *
 * Über einem Riff sagt das Luftbild mehr als jede Karte; man sieht die
 * Riffkante, an der man entlanggetaucht ist. Über einem Schweizer See ist es
 * umgekehrt. Deshalb umschaltbar, und die Wahl bleibt stehen (`localStorage`) —
 * niemand will sie bei jedem Tauchgang neu treffen.
 *
 * **Eine Stelle für drei Karten.** Die Adresse des Kachelservers, die
 * Herkunftsangabe und der Speicherschlüssel standen sonst dreimal da:
 * Detailseite, Weltkarte, Verorten. Wer die Adresse wechselt, wechselt sie
 * einmal.
 *
 * Umgefärbt wird nur der Kartengrund, nie das Luftbild — der Filter hängt
 * deshalb an der Ebene (`grund-karte`), nicht am Behälter. Ein invertiertes
 * Luftbild wäre unlesbar.
 */

import L from 'leaflet';

export const GRUENDE = {
  karte: {
    name: 'Karte',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    herkunft: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
    klasse: 'grund-karte',
  },
  satellit: {
    name: 'Satellit',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/'
      + 'MapServer/tile/{z}/{y}/{x}',
    herkunft: 'Luftbild © <a href="https://www.esri.com">Esri</a>, Maxar, Earthstar Geographics',
    maxZoom: 19,
    klasse: 'grund-satellit',
  },
};

const SCHLUESSEL = 'tauchlogbuch:kartengrund';
// Ein Tauchlogbuch zeigt Karten ueber Wasser, und ueber Wasser ist ein
// Luftbild schwarz — kein Ufer, keine Beschriftung, nichts. Die Karte zeigt
// dort Blau, Kuestenlinie und Namen. Das Luftbild bleibt einen Tipp entfernt:
// Ueber einem Riff sagt es mehr als jede Karte, aber es ist die Ausnahme,
// nicht der Anfang.
const VORGABE = 'karte';

/** Die Wahl überlebt den Reiterwechsel — aber nie einen Fehler im Speicher. */
export function geleseneWahl() {
  try {
    const wert = globalThis.localStorage?.getItem(SCHLUESSEL);
    return wert && GRUENDE[wert] ? wert : VORGABE;
  } catch {
    return VORGABE;   // privater Modus, abgeschalteter Speicher — beides egal
  }
}

function merkeWahl(wahl) {
  try {
    globalThis.localStorage?.setItem(SCHLUESSEL, wahl);
  } catch { /* nicht der Rede wert */ }
}

/** Die Knopfleiste als Auszeichnung — die Ansicht setzt sie, wohin sie passt. */
export function grundwahlLeiste() {
  return `<div class="grundwahl" role="group" aria-label="Kartengrund">${
    Object.entries(GRUENDE).map(([schluessel, grund]) =>
      `<button type="button" data-grund="${schluessel}">${grund.name}</button>`).join('')
  }<span class="grundmeldung" role="status" aria-live="polite"></span></div>`;
}

// Erst nach ein paar Fehlschlägen melden: Am Rand des Ausschnitts scheitert
// immer mal eine Kachel, ohne dass etwas kaputt wäre.
const FEHLER_BIS_MELDUNG = 3;

/**
 * Verbindet eine Knopfleiste mit einer Karte.
 *
 * Die Karte darf noch fehlen — Leaflet braucht ein Element, das schon im
 * Dokument hängt, und wird deshalb überall erst später gebaut. Die Knöpfe
 * stehen trotzdem gleich richtig; `anKarte()` holt die Kachelebene nach.
 */
export function grundwahl(leiste) {
  let wahl = geleseneWahl();
  let karte = null;
  let ebene = null;
  const meldung = leiste.querySelector('.grundmeldung');

  function zeichneKnoepfe() {
    for (const knopf of leiste.querySelectorAll('button[data-grund]')) {
      const aktiv = knopf.dataset.grund === wahl;
      knopf.classList.toggle('aktiv', aktiv);
      knopf.setAttribute('aria-pressed', String(aktiv));
    }
  }

  /**
   * Ein leerer Kartengrund sagt nicht, warum er leer ist.
   *
   * Kein Netz, ein gesperrter Anbieter, eine Adresse, die es nicht mehr gibt —
   * alles sieht gleich aus: dunkle Fläche mit Markern darauf. Wer das zum
   * ersten Mal sieht, sucht den Fehler bei sich. Kommt keine einzige Kachel an,
   * steht es deshalb dabei; die Marker liegen ohnehin richtig, das gehört
   * mitgesagt, damit niemand die Ansicht für kaputt hält.
   *
   * **Der Grund wird nicht geraten.** `navigator.onLine` sagt nur, ob eine
   * Netzwerkschnittstelle da ist, nicht ob das Internet antwortet: In einer
   * abgeschotteten Umgebung steht es auf `true`, während keine einzige Kachel
   * ankommt. Daraus "Anbieter nicht erreichbar" zu folgern, wäre eine
   * Behauptung ins Blaue. Genannt werden deshalb beide Möglichkeiten.
   */
  function pruefeKacheln(geladen, fehlerhaft) {
    if (!meldung) return;
    if (geladen > 0 || fehlerhaft < FEHLER_BIS_MELDUNG) {
      meldung.textContent = '';
      return;
    }
    meldung.textContent = `${GRUENDE[wahl].name} lädt nicht — kein Netz oder `
      + 'Anbieter nicht erreichbar. Marker stimmen.';
  }

  function setzeEbene() {
    if (!karte) return;
    ebene?.remove();
    const grund = GRUENDE[wahl];
    ebene = L.tileLayer(grund.url, {
      attribution: grund.herkunft, maxZoom: grund.maxZoom, className: grund.klasse,
    }).addTo(karte);
    // Der Grund gehört unter alles andere: Ohne das läge eine später
    // gewechselte Kachelebene über den Markern.
    ebene.bringToBack();

    let geladen = 0;
    let fehlerhaft = 0;
    if (meldung) meldung.textContent = '';
    ebene.on('tileload', () => { geladen += 1; pruefeKacheln(geladen, fehlerhaft); });
    ebene.on('tileerror', () => { fehlerhaft += 1; pruefeKacheln(geladen, fehlerhaft); });
  }

  leiste.addEventListener('click', (ereignis) => {
    const knopf = ereignis.target.closest('button[data-grund]');
    if (!knopf || knopf.dataset.grund === wahl) return;
    wahl = knopf.dataset.grund;
    merkeWahl(wahl);
    zeichneKnoepfe();
    setzeEbene();
  });

  zeichneKnoepfe();
  return {
    anKarte(neueKarte) {
      karte = neueKarte;
      setzeEbene();
    },
    aktuell: () => wahl,
  };
}

/**
 * Die Karte über ihre eigene Grösse auf dem Laufenden halten.
 *
 * Leaflet misst seinen Behälter beim Anlegen und nie wieder von selbst. Solange
 * eine Ansicht synchron eingehängt wurde, ging das gut. Seit die Ansichten mit
 * einem Übergang gewechselt werden, hängt der Behälter im Augenblick des
 * Anlegens noch nicht im Dokument — die Karte bliebe null Pixel gross und die
 * Kacheln kämen nie. Ein `ResizeObserver` meldet die erste echte Grösse und
 * jede spätere: gedrehtes Telefon, aufgeklappte Tastatur, umgeschaltete
 * Fenstergrösse.
 */
export function haltGroesseNach(karte, behaelter, ausschnitt = null,
  optionen = { padding: [30, 30] }) {
  // Der Ausschnitt wird erst gesetzt, wenn der Behaelter eine echte Groesse
  // hat. Wer ihn vorher setzt, rechnet ihn aus einer Hoehe von null — Leaflet
  // faellt dann auf die hoechste Zoomstufe zurueck, und die Weltkarte oeffnete
  // am Telefon auf Strassenebene (gemessen: Stufe 19 statt 3, waehrend
  // dieselbe Ansicht am Schreibtisch richtig aufging, weil dort die Hoehe von
  // Anfang an feststand).
  //
  // `invalidateSize` allein reicht nicht: Es ruecke die Groesse zurecht, den
  // Zoom aber nicht mehr. Deshalb einmal nachfassen — und nur einmal, sonst
  // risse es dem Benutzer die Karte unter dem Finger weg, sobald sich etwas
  // an der Groesse aendert (Drehen, Tastatur, aufklappende Leiste).
  let angepasst = ausschnitt === null;
  const beobachter = new ResizeObserver(() => {
    if (behaelter.clientWidth > 0 && behaelter.clientHeight > 0) {
      karte.invalidateSize({ animate: false });
      if (!angepasst) {
        angepasst = true;
        karte.fitBounds(ausschnitt, { ...optionen, animate: false });
      }
    }
  });
  beobachter.observe(behaelter);
  return () => beobachter.disconnect();
}
